import {
  Avatar,
  Box,
  type BoxProps,
  type ButtonProps,
  HStack,
  type StackProps,
  Text,
  type TextProps,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { FiClock } from "react-icons/fi";
import Select from "../../../shared/components/Select";
import { entityProfileModalBorders, entityProfileModalColors } from "../entityProfileModal.theme";

export type EntityProfileRecentActivityTimelineItem = {
  actorName: string;
  actorPhotoUrl?: string;
  category: string;
  content: React.ReactNode;
  date: React.ReactNode;
  id: React.Key;
};

type Props = {
  categories: string[];
  items: EntityProfileRecentActivityTimelineItem[];
  maxH?: StackProps["maxH"];
  onSelectedCategoriesChange: (value: string[]) => void;
  selectedCategories: string[];
};

const categorySelectButtonProps: ButtonProps = {
  _active: { bg: "#dcdcdc" },
  _expanded: { bg: "#e5e5e5" },
  _hover: { bg: "#fff" },
  bg: "#fff",
  border: "1px solid #cfcfcf",
  borderRadius: "2px",
  color: "#111827",
  colorScheme: undefined,
  fontSize: "14px",
  fontWeight: 400,
  h: "34px",
  justifyContent: "center",
  minW: "223px",
};

const filterContainerProps: BoxProps = {
  borderBottom: entityProfileModalBorders.default,
  pb: 4,
};

const filterLabelProps: TextProps = {
  color: entityProfileModalColors.text,
  fontSize: "13px",
  fontWeight: 700,
  mb: 2,
};

const timelineProps: StackProps = {
  alignItems: "stretch",
  spacing: 0,
};

const rowProps: StackProps = {
  alignItems: "flex-start",
  borderBottom: entityProfileModalBorders.default,
  justifyContent: "space-between",
  minH: "92px",
  py: 4,
  spacing: 6,
};

const actorColumnProps: StackProps = {
  alignItems: "flex-start",
  minW: 0,
  spacing: 3,
};

const avatarWrapperProps: BoxProps = {
  flexShrink: 0,
  pt: "1px",
};

const contentProps: BoxProps = {
  color: entityProfileModalColors.text,
  fontSize: "14px",
  lineHeight: "26px",
  minW: 0,
};

const actorNameProps: TextProps = {
  as: "span",
  fontWeight: 700,
};

const metaProps: StackProps = {
  alignItems: "center",
  flexShrink: 0,
  justifyContent: "flex-end",
  minH: "34px",
  spacing: 2,
  whiteSpace: "nowrap",
};

const metaDateProps: TextProps = {
  color: entityProfileModalColors.text,
  fontSize: "14px",
};

const categoryPillProps: BoxProps = {
  as: "span",
  border: "1px solid #3077db",
  borderRadius: "24px",
  color: "#3077db",
  display: "inline-block",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "1",
  px: 4,
  py: "7px",
  whiteSpace: "nowrap",
};

function CategoryFilter(props: {
  categories: string[];
  onChange: (value: string[]) => void;
  selectedCategories: string[];
}) {
  return (
    <Box {...filterContainerProps}>
      <Text {...filterLabelProps}>Sections</Text>
      <Select
        multiple
        buttonProps={categorySelectButtonProps}
        closeOnSelectAll={false}
        closeOnUnselect={false}
        label="Select"
        maxH="320px"
        options={props.categories.map((category) => ({ label: category, value: category }))}
        searchable={false}
        selectedLabel={(value) => (value.length === 0 ? "Select" : `${value.length} selected`)}
        size="sm"
        value={props.selectedCategories.length === 0 ? null : props.selectedCategories}
        width="223px"
        onChange={(value) => props.onChange(value ?? [])}
      />
    </Box>
  );
}

function CategoryPill(props: { category: string }) {
  return <Box {...categoryPillProps}>{props.category}</Box>;
}

export default function EntityProfileRecentActivityTimeline(props: Props) {
  return (
    <VStack alignItems="stretch" maxH={props.maxH} spacing={4}>
      <CategoryFilter
        categories={props.categories}
        selectedCategories={props.selectedCategories}
        onChange={props.onSelectedCategoriesChange}
      />

      <VStack {...timelineProps}>
        {props.items.map((item) => (
          <HStack key={item.id} {...rowProps}>
            <HStack {...actorColumnProps}>
              <Box {...avatarWrapperProps}>
                <Avatar name={item.actorName} size="sm" src={item.actorPhotoUrl} />
              </Box>
              <Box {...contentProps}>
                <Text {...actorNameProps}>{item.actorName}</Text> {item.content}
              </Box>
            </HStack>

            <HStack {...metaProps}>
              <CategoryPill category={item.category} />
              <FiClock size={16} />
              <Text {...metaDateProps}>{item.date}</Text>
            </HStack>
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
}
